/**
 * GiftModal — V2.8.8 — pick a recipient, send a SKU as a gift.
 * Used from both the Store (after clicking Gift on a SKU) and PublicProfile
 * (Send Gift icon, which pre-selects the recipient and asks for SKU).
 */
import { useEffect, useState } from "react";
import api, { fmtErr } from "@/lib/api";

export default function GiftModal({ initialSku = "", initialRecipient = null, onClose, onSent }) {
  const [sku, setSku] = useState(initialSku);
  const [recipient, setRecipient] = useState(initialRecipient); // { id, name, avatar }
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/store/catalog"); setCatalog(data); } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!query || query.length < 2 || recipient) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/social/search/users`, { params: { q: query, limit: 8 } }); setResults(data || []); }
      catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, recipient]);

  async function send() {
    if (!sku || !recipient?.id) { setErr("Pick an item and a recipient."); return; }
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await api.post("/store/gift", { sku, recipient_id: recipient.id });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      setMsg(data.message || "Gift delivered!");
      setTimeout(() => { onSent && onSent(); onClose && onClose(); }, 1500);
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  const item = catalog.find(c => c.id === sku);

  return (
    <div data-testid="gift-modal" className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 font-mono">
      <div className="bg-[#0f131b] border border-amber-500/30 rounded-2xl max-w-md w-full p-5 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2">
            <img src="/ui/gift.png" alt="" className="w-7 h-7"/>
            <div>
              <div className="text-base font-bold text-white">Send a Gift</div>
              <div className="text-[10px] text-slate-500">Gifts are final — not refundable.</div>
            </div>
          </div>
          <button data-testid="gift-close" onClick={onClose} className="text-slate-500 hover:text-rose-300 text-xl"><i className="fa-solid fa-xmark"/></button>
        </div>

        {msg && <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded p-2">{msg}</div>}
        {err && <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-2" data-testid="gift-error">{err}</div>}

        {/* Item picker */}
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Item</div>
          {item ? (
            <div className="flex items-center gap-3 bg-slate-900/40 border border-slate-800 rounded-lg p-2.5">
              {item.image_url && <img src={item.image_url} alt="" className="w-10 h-10 object-contain"/>}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white truncate">{item.name}</div>
                <div className="text-[10px] text-amber-300 font-black">${item.price_usd}</div>
              </div>
              <button data-testid="gift-clear-sku" onClick={() => setSku("")} className="text-[10px] text-slate-500 hover:text-rose-300">Change</button>
            </div>
          ) : (
            <select data-testid="gift-sku-select" value={sku} onChange={e => setSku(e.target.value)}
              className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm">
              <option value="">Pick an item…</option>
              {catalog.map(c => <option key={c.id} value={c.id}>{c.name} — ${c.price_usd}</option>)}
            </select>
          )}
        </div>

        {/* Recipient picker */}
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Send to</div>
          {recipient ? (
            <div className="flex items-center gap-3 bg-slate-900/40 border border-slate-800 rounded-lg p-2.5">
              {recipient.avatar
                ? <img src={recipient.avatar} alt="" className="w-9 h-9 rounded-full object-cover"/>
                : <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-sm font-black text-slate-300">{(recipient.name||"?").charAt(0).toUpperCase()}</div>}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white truncate" data-testid="gift-recipient-name">{recipient.name}</div>
              </div>
              <button data-testid="gift-clear-recipient" onClick={() => { setRecipient(null); setQuery(""); }} className="text-[10px] text-slate-500 hover:text-rose-300">Change</button>
            </div>
          ) : (
            <>
              <input data-testid="gift-search" value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Search by username or email…"
                className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm"/>
              {results.length > 0 && (
                <div className="mt-1 bg-slate-900/40 border border-slate-800 rounded-lg max-h-44 overflow-y-auto">
                  {results.map(r => (
                    <button key={r.id} data-testid={`gift-pick-${r.id}`} onClick={() => setRecipient(r)}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-800/60 text-left">
                      {r.avatar
                        ? <img src={r.avatar} alt="" className="w-7 h-7 rounded-full object-cover"/>
                        : <div className="w-7 h-7 rounded-full bg-slate-700 text-xs font-black text-slate-300 flex items-center justify-center">{(r.name||"?").charAt(0).toUpperCase()}</div>}
                      <span className="text-xs text-white">{r.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <button data-testid="gift-send" disabled={busy || !sku || !recipient?.id} onClick={send}
          className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-400 hover:to-rose-400 text-slate-900 rounded-lg text-xs font-black uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? "Sending…" : <><i className="fa-solid fa-paper-plane mr-1.5"/>Send Gift</>}
        </button>
      </div>
    </div>
  );
}
